import { useEffect, useMemo, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import Card from "~/common/components/Card";

import { IconType } from "~/common/components/icon/IconType";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import { Link, LinkButton, LinkType } from "~/common/components/Link";
import { Col, Row } from "~/common/components/Layout";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { type EditUserPageProps } from "~/features/userAdministration/editUser/models/EditUserAccountModels";
import { SimpleMultiSelect } from "~/common/components/Select/SimpleMultiSelect";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Controller, type FieldError, useForm, type UseFormSetValue } from "react-hook-form";
import { FormSection } from "~/pages/MyAccountPage/MyAccountPage";
import { makeSelectRef4RHF, useConfirmBeforeUnload } from "~/features/myAccount/profile/formUtils";
import Parameter from "~/common/components/Parameter";
import { MandatoryMessage } from "~/features/myAccount/profile/components/common";
import { PHONE_REGEXP, ZIPCODE_MAX_LENGTH, ZIPCODE_MIN_LENGTH, ZIPCODE_REGEXP } from "~/common/utils/validations";
import { MANAGE_USERS_PAGE } from "~/utils/url/staticUrls";
import { type UserAccountValues } from "~/features/userAdministration/editUser/types/userAccountTypes";
import { type DropdownOption } from "~/features/myAccount/profile/components/MyAccountB2B/MyAccountB2B";
import { actionsUserAccount } from "~/features/userAdministration/editUser/api/createEditMyAccount";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { TrainingConsent } from "~/features/myAccount/profile/components/TrainingConsent";
import { useTranslation } from "react-i18next";
import { type ItemDef } from "~/common/types";
import { ModalConfirmation } from "~/features/myAccount/profile/components/ModalConfirmation";
import {
  CUSTOMER_CATEGORY,
  DISTRIBUTOR_CATEGORY,
  EMAIL_PATTERN,
  SCHAEFFLER_INTERNAL_CATEGORY,
  SERVICE_PROVIDER_CATEGORY,
} from "~/utils/constants";
import { Button, Stack, TextField } from "~/common/mui";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import TrainingConsentInfo from "~/features/myAccount/profile/components/TrainingConsentInfo/TrainingConsentInfo";
import { Alert } from "~/common/mui/Alert/Alert";
import type { EditUserAccountsDTO } from "~/features/userAdministration/editUser/dto/EditUserAccountDTO";
import { transformData } from "~/features/userAdministration/editUser/transformers/UserAccountsTransformer";

const EditUserAccountsPage = (props: EditUserPageProps) => {
  const { t } = useTranslation();

  const {
    fields,
    isSessionUser,
    roleDescriptions,
    uid,
    dropdownData,
    internalDomains,
    defaultB2BUnit,
    serviceProviderAccounts,
    distributorAccounts,
  } = props;

  const { val: modalOpen, setTrue: open, setFalse: close } = useBooleanState(false);

  const [trainingCheckboxTouched, setTrainingCheckboxTouched] = useState(false);
  const { val: trainingModal, setTrue: showTrainingModal, setFalse: hideTrainingModal } = useBooleanState(false);

  const haveInitialCategory = !!fields.customerCategoryCode;
  const isCreate = fields.email === "";
  const { val: modalCategoryOpen, setTrue: openModalCategory, setFalse: closeModalCategory } = useBooleanState(false);

  const formFields = useMemo(() => {
    const fieldKeys = Object.keys(fields).filter((key) => key !== "endCustomerCodes");

    const stateFields = fieldKeys.reduce((fieldMap, key) => {
      const objKey = key as keyof UserAccountValues;
      fieldMap[objKey] = fields[objKey];
      return fieldMap;
    }, {} as UserAccountValues);

    stateFields.endCustomerCodes = fields.endCustomerCodes ? fields.endCustomerCodes.split(" ").filter(Boolean) : [];
    // SCHAEFF-9348 - if user has only 1 account multiselect should not be displayed and account should be preselected
    if (dropdownData.accounts.length === 1) {
      stateFields.accountCodes = [dropdownData.accounts[0]?.value];
      stateFields.endCustomerCodes = dropdownData.accounts[0]?.endCustomerCodes.map(
        (endCustomerCode) => endCustomerCode.endCustomerCode,
      );
    } else {
      stateFields.accountCodes = fields.accountCodes.split(" ").filter(Boolean);
    }

    // For new users, the training checkbox is checked by default.
    if (!uid) {
      stateFields.lmsRequest = true;
    }

    return stateFields;
  }, [fields]);

  const salutationsArray = useMemo(() => {
    const remodeledSalutation: DropdownOption[] = [];

    dropdownData.salutationOptions.forEach((salutation) => {
      const labelTranslated = t(salutation.label);
      remodeledSalutation.push({ value: salutation.value, label: labelTranslated });
    });

    return remodeledSalutation;
  }, [dropdownData]);

  const titlesArray = useMemo(() => {
    const remodeledTitles: DropdownOption[] = [];

    dropdownData.titleOptions.forEach((title) => {
      const labelTranslated = t(title.label);
      remodeledTitles.push({ value: title.value, label: labelTranslated });
    });

    return remodeledTitles;
  }, [dropdownData]);

  const countriesArray = useMemo(() => {
    const remodeledSalutation: DropdownOption[] = [];

    dropdownData.countryOptions.forEach((country) => {
      const labelTranslated = t(country.label);
      remodeledSalutation.push({ value: country.value, label: labelTranslated });
    });

    return remodeledSalutation;
  }, [dropdownData]);

  const jobTitlesArray = useMemo(() => {
    const remodeledJobTitles: DropdownOption[] = [];

    dropdownData.jobTitleOptions.forEach((country) => {
      const labelTranslated = t(country.label);
      remodeledJobTitles.push({ value: country.value, label: labelTranslated });
    });

    return remodeledJobTitles;
  }, [dropdownData]);

  const functionalAreasArray = useMemo(() => {
    const remodeledfunctionalAreas: DropdownOption[] = [];

    dropdownData.functionalAreaOptions.forEach((country) => {
      const labelTranslated = t(country.label);
      remodeledfunctionalAreas.push({ value: country.value, label: labelTranslated });
    });

    return remodeledfunctionalAreas;
  }, [dropdownData]);

  const accountsArray = useMemo(() => {
    const remodeledAccounts: ItemDef[] = [];

    dropdownData.accounts.forEach((account) => {
      remodeledAccounts.push({ label: t(account.label), code: account.value });
    });

    return remodeledAccounts;
  }, [dropdownData]);

  const rolesArray = useMemo(() => {
    const remodeledRoles: DropdownOption[] = [];

    dropdownData.roles.forEach((account) => {
      remodeledRoles.push({ label: t(account.label), value: account.value });
    });

    return remodeledRoles;
  }, [dropdownData]);

  const statusArray = useMemo(() => {
    const remodeledStatus: DropdownOption[] = [];

    dropdownData.statusOptions.forEach((account) => {
      remodeledStatus.push({ label: t(account.label), value: account.value });
    });

    return remodeledStatus;
  }, [dropdownData]);
  const { handleSubmit, control, reset, setValue, setError, getValues, watch, formState } = useForm<UserAccountValues>({
    defaultValues: formFields,
    reValidateMode: "onBlur",
  });

  const [isEnableFieldsTraining, setIsEnableFieldsTraining] = useState<boolean>(uid ? formFields.lmsRequest : true);
  const customerCategoryCode = watch("customerCategoryCode");
  const customerCategoryLabel =
    dropdownData.customerCategoryOptions.find((item) => item.value === customerCategoryCode)?.label ?? "";

  useConfirmBeforeUnload(formState);

  const onSubmit = async (data: UserAccountValues, confirmed: boolean) => {
    if (!haveInitialCategory && !!customerCategoryCode && !confirmed && isEnableFieldsTraining) {
      return openModalCategory();
    } else {
      closeModalCategory();
      if (trainingCheckboxTouched) {
        setTrainingCheckboxTouched(false);
        showTrainingModal();
      }
    }
    try {
      const errors = await actionsUserAccount(data, !isCreate, isCreate, uid);
      if (!errors.length) {
        reset(getValues());
        if (!trainingCheckboxTouched) {
          redirectToUrl(MANAGE_USERS_PAGE);
        }
      } else {
        setError("email", { message: errors[0] });
      }
    } catch (e) {
      alert((e as Error).toString());
    }
  };

  const onDelete = async () => {
    const data = getValues();
    reset(getValues());
    try {
      await actionsUserAccount(data, false, false, uid);
      redirectToUrl(MANAGE_USERS_PAGE);
    } catch (e) {
      alert((e as Error).toString());
    }
  };

  const getLabel = (name: string, required?: boolean) =>
    t(`page.myAccount.addEditUser.${name}`) + (required ? "*" : "");

  const getSelectableEndCustomerValuesForAccounts = (accountCodes: string[]) => {
    return dropdownData.accounts
      .filter((account) => accountCodes.includes(account.value))
      .flatMap((account) => account.endCustomerCodes)
      .map((account) => account.endCustomerCode);
  };

  const sameUser = fields.email === window.SCHA.user.email;
  const emailForm = watch("email");
  const isSchaefflerEmail =
    (internalDomains != null && internalDomains.some((domain) => domain.domain === emailForm.split("@")[1])) ?? false;

  useEffect(() => {
    if (!isCreate) {
      return;
    }

    if (isSchaefflerEmail) {
      if (customerCategoryCode !== SCHAEFFLER_INTERNAL_CATEGORY) {
        setValue("customerCategoryCode", SCHAEFFLER_INTERNAL_CATEGORY);
      }
    } else {
      resolveCustomerCategoryForAccounts(
        watch("accountCodes"),
        defaultB2BUnit,
        serviceProviderAccounts,
        distributorAccounts,
        setValue,
      );
    }
  }, [isCreate, emailForm]);

  const renderSelectAccountDropdown = () => {
    const fieldState = formFields.accountCodes;
    if (accountsArray.length <= 1) {
      // if user has only himself in multiselect options and no endCustomers on account
      return null; // don't display multiselect
    } else {
      return (
        <Controller
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <SimpleMultiSelect
              values={accountsArray}
              value={value}
              label={getLabel("account", true)}
              data-test-id="assignAccountMultiselect"
              onChange={(e) => {
                const oldState = fieldState;
                const newState = e.target.value as unknown as string[];
                const addedValues = newState.filter((val) => !oldState.includes(val));
                const removedValues = oldState.filter((val: string) => !newState.includes(val));

                const endCustomersToAdd = getSelectableEndCustomerValuesForAccounts(addedValues);
                const endCustomersToRemove = getSelectableEndCustomerValuesForAccounts(removedValues);
                onChange(e.target.value);

                const newEndCustomers = formFields.endCustomerCodes
                  .filter((code) => !endCustomersToRemove.includes(code))
                  .concat(...endCustomersToAdd);
                setValue("endCustomerCodes", newEndCustomers);
                setValue("accountCodes", newState);

                if (!isSchaefflerEmail) {
                  resolveCustomerCategoryForAccounts(
                    addedValues,
                    defaultB2BUnit,
                    serviceProviderAccounts,
                    distributorAccounts,
                    setValue,
                  );
                }
              }}
              fullWidth={true}
              error={!!error}
              helperText={error?.message && t(error.message)}
            />
          )}
          name={"accountCodes"}
          control={control}
          rules={{ required: "profile.accounts.empty" }}
        />
      );
    }
  };

  const renderRoles = () => {
    const values = roleDescriptions;
    if (!values || values.length === 0) {
      return;
    }
    return (
      <div className="scha__edit-user-accounts__roles--wrapper">
        {values.map((val) => (
          <div className="scha__edit-user-accounts__roles__info">
            <div className="scha__edit-user-accounts__roles__info__name">{t(val.name)}</div>
            <div className="scha__edit-user-accounts__roles__info__description">{t(val.description)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <TrainingConsentInfo
        modal={trainingModal}
        closeModal={hideTrainingModal}
        onActivated={() => {
          redirectToUrl(MANAGE_USERS_PAGE);
        }}
      />
      <PageTitle title={uid ? t("addon.text.company.manage.unit.user.edit") : t("myCompany.form.addUser")} />
      <div className="scha__edit-user-accounts">
        <form
          onSubmit={handleSubmit((data) => onSubmit(data, false))}
          id={"complete-form"}
          className="scha__edit-user-accounts__form"
        >
          <Row className="scha__edit-user-accounts__form">
            <Col size={{ sm: 12, md: 4 }} className={"d-flex flex-direction-column"}>
              <FormSection title={t("myAccount.name.title")}>
                <Controller
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={titlesArray}
                      label={t("profile.title")}
                      error={!!error}
                      fullWidth={true}
                      data-test-id={"my-account-add-edit-title"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"title"}
                  control={control}
                  rules={{ required: false }}
                />

                <Controller
                  render={({ field: { value, onChange, onBlur, ref }, fieldState: { invalid, error } }) => (
                    <SimpleSelect
                      values={salutationsArray}
                      label={t("page.completeRegistration.salutation") + "*"}
                      error={invalid}
                      fullWidth={true}
                      data-test-id={"my-account-add-edit-salutation"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      ref={makeSelectRef4RHF(ref)}
                      helperText={error?.message ?? true}
                    />
                  )}
                  name={"salutation"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                />

                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={t("page.completeRegistration.firstName") + "*"}
                      inputProps={{ "data-test-id": "my-account-add-edit-firstName" }}
                      inputRef={ref}
                      error={!!error}
                      helperText={error?.message ?? undefined}
                      {...inputProps}
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={t("page.completeRegistration.lastName") + "*"}
                      inputProps={{ "data-test-id": "my-account-add-edit-lastName" }}
                      error={!!error}
                      helperText={error?.message ?? undefined}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                />

                {fields.email === "" ? (
                  <Controller
                    name={"email"}
                    control={control}
                    rules={{
                      required: true,
                      pattern: { value: EMAIL_PATTERN, message: "error.invalidEmail" },
                    }}
                    render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        label={t("page.completeRegistration.email") + "*"}
                        inputProps={{ "data-test-id": "my-account-add-edit-email" }}
                        autoComplete={"email"}
                        inputRef={ref}
                        error={!!error}
                        helperText={<HelperLabelErrorMessage error={error} />}
                        {...inputProps}
                      />
                    )}
                  />
                ) : (
                  <Parameter
                    label={t("page.completeRegistration.email")}
                    value={<div className="title-medium">{formFields.email}</div>}
                  />
                )}

                <Controller
                  name={"phone"}
                  control={control}
                  render={({ field: { ref, ...inputProps } }) => (
                    <TextField
                      fullWidth
                      label={getLabel("phone")}
                      onInput={(e) => {
                        // @ts-ignore
                        const val = e.target.value; // onInput event target has value
                        if (!PHONE_REGEXP.test(val.trim())) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      // onBeforeInput is not standard in React for TextField?

                      // TextField inputProps can take onBeforeInput?
                      inputProps={{
                        "data-test-id": "my-account-add-edit-phone",
                        onBeforeInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          if (!PHONE_REGEXP.test(val.trim())) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        },
                      }}
                      autoComplete={"tel"}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                />

                <Alert severity={"info"}>{t("addon.user.disclaimer")}</Alert>
              </FormSection>
            </Col>

            <Col size={{ sm: 12, md: 4 }} className={"d-flex flex-direction-column"}>
              <FormSection title={t("myAccount.company.title")}>
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={statusArray}
                      label={getLabel("status")}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"my-account-add-edit-language"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"status"}
                  control={control}
                />

                <Controller
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <SimpleSelect
                      label={t("addedituser.role.label") + "*"}
                      fullWidth={true}
                      values={rolesArray}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      error={!!error}
                      disabled={sameUser}
                      data-test-id={"my-account-add-edit-role"}
                    />
                  )}
                  name={"role"}
                  control={control}
                  rules={{ required: true }}
                />

                {renderSelectAccountDropdown()}

                <Controller
                  render={({ field: { value, onChange, onBlur } }) => (
                    <SimpleSelect
                      values={functionalAreasArray}
                      label={getLabel("functionalArea")}
                      fullWidth={true}
                      data-test-id={"my-account-add-edit-functional-area"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"functionalArea"}
                  control={control}
                />
                <Controller
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={jobTitlesArray}
                      label={getLabel("jobTitle", false)}
                      fullWidth={true}
                      data-test-id={"my-account-add-edit-job-title"}
                      onBlur={onBlur}
                      value={value}
                      error={!!error}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"jobTitle"}
                  control={control}
                />
                {isEnableFieldsTraining && (
                  <Controller
                    name={"address"}
                    control={control}
                    rules={{
                      required: isEnableFieldsTraining,
                    }}
                    render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        label={getLabel("address", isEnableFieldsTraining)}
                        inputProps={{ "data-test-id": "my-account-add-edit-address" }}
                        autoComplete={"address-line1"}
                        disabled={isEnableFieldsTraining}
                        inputRef={ref}
                        error={!!error}
                        {...inputProps}
                      />
                    )}
                  />
                )}

                {isEnableFieldsTraining && (
                  <div>
                    <div className="scha__my-account__form__inputs__zip-city">
                      <div className={"scha__my-account__form__inputs__zip-city__zip"}>
                        <Controller
                          name={"zipCode"}
                          control={control}
                          rules={{
                            minLength: {
                              value: ZIPCODE_MIN_LENGTH,
                              message: t("checkoutPage.addressModal.error.isTooShortPart", {
                                fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                              }),
                            },
                            maxLength: {
                              value: ZIPCODE_MAX_LENGTH,
                              message: t("checkoutPage.addressModal.error.isTooLongPart", {
                                fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                              }),
                            },
                            pattern: {
                              value: ZIPCODE_REGEXP,
                              message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
                                fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                              }),
                            },
                            required: isEnableFieldsTraining,
                          }}
                          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                            <TextField
                              fullWidth
                              label={getLabel("zipCode", isEnableFieldsTraining)}
                              inputProps={{
                                "data-test-id": "my-account-add-edit-zipCode",
                                onBeforeInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  if (!ZIPCODE_REGEXP.test(val.trim())) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }
                                },
                              }}
                              error={!!error}
                              helperText={error?.message}
                              disabled={isEnableFieldsTraining}
                              autoComplete={"postal-code"}
                              inputRef={ref}
                              {...inputProps}
                            />
                          )}
                        />
                      </div>

                      <div className="scha__my-account__form__inputs__zip-city__city">
                        <Controller
                          rules={{
                            required: isEnableFieldsTraining,
                          }}
                          name={"city"}
                          control={control}
                          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                            <TextField
                              fullWidth
                              label={getLabel("city", isEnableFieldsTraining)}
                              inputProps={{ "data-test-id": "my-account-add-edit-city" }}
                              autoComplete={"city"}
                              error={!!error}
                              disabled={isEnableFieldsTraining}
                              inputRef={ref}
                              {...inputProps}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {isEnableFieldsTraining && (
                  <Controller
                    name={"country"}
                    control={control}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SimpleSelect
                        values={countriesArray}
                        label={getLabel("country", true)}
                        fullWidth={true}
                        data-test-id={"complete-registration-country"}
                        onBlur={onBlur}
                        value={value}
                        error={!!error}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={isEnableFieldsTraining}
                      />
                    )}
                    rules={{ required: isEnableFieldsTraining }}
                  />
                )}

                {isEnableFieldsTraining && (
                  <Controller
                    name={"vatNumber"}
                    control={control}
                    render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        label={getLabel("vatNumber")}
                        helperText={getLabel("exampleVat")}
                        inputProps={{ "data-test-id": "my-account-add-edit-vat" }}
                        error={!!error}
                        inputRef={ref}
                        {...inputProps}
                      />
                    )}
                  />
                )}
              </FormSection>
            </Col>
            <Col size={{ sm: 12, md: 4 }} className={"d-flex flex-direction-column"}>
              <div className="scha__edit-user-accounts__training">
                <Controller
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <TrainingConsent
                      checked={value}
                      onCheck={(e) => {
                        setIsEnableFieldsTraining(e);
                        setTrainingCheckboxTouched(e);
                        onChange(e);
                      }}
                      error={!!error}
                      disabled={uid ? formFields.lmsRequest : true}
                    />
                  )}
                  name={"lmsRequest"}
                  control={control}
                />

                {isEnableFieldsTraining && (
                  <>
                    <p className="my-30 f-15">{t("myAccount.training.welcome")}</p>
                    <Stack gap={2}>
                      <Controller
                        rules={{
                          required: isEnableFieldsTraining,
                        }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SimpleSelect
                            values={dropdownData.languageOptions}
                            label={getLabel("language", isEnableFieldsTraining)}
                            fullWidth={true}
                            error={!!error}
                            data-test-id={"my-account-add-edit-language"}
                            onBlur={onBlur}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                          />
                        )}
                        name={"languageIsoCode"}
                        control={control}
                      />

                      {!haveInitialCategory && (
                        <Alert severity={"info"}>{t("myAccount.customerCategory.info.text")}</Alert>
                      )}
                      <Controller
                        rules={{
                          required: isEnableFieldsTraining,
                        }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SimpleSelect
                            values={dropdownData.customerCategoryOptions}
                            label={getLabel("customerCategory", isEnableFieldsTraining)}
                            fullWidth={true}
                            error={!!error}
                            data-test-id={"my-account-add-edit-customer-category"}
                            onBlur={onBlur}
                            value={value}
                            disabled={isSchaefflerEmail || haveInitialCategory}
                            onChange={(e) => onChange(e.target.value)}
                          />
                        )}
                        name={"customerCategoryCode"}
                        control={control}
                      />

                      <Controller
                        rules={{
                          required: isEnableFieldsTraining,
                        }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SimpleSelect
                            values={dropdownData.knowledgeAreaOptions}
                            label={getLabel("knowledgeArea", isEnableFieldsTraining)}
                            fullWidth={true}
                            error={!!error}
                            data-test-id={"my-account-add-edit-knowledge-area"}
                            onBlur={onBlur}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                          />
                        )}
                        name={"knowledgeAreaCode"}
                        control={control}
                      />
                    </Stack>
                  </>
                )}
              </div>
            </Col>
          </Row>

          <MandatoryMessage className={"scha__edit-user-accounts__mandatory "} />

          {/* BUTTONS*/}
          <div className="scha__edit-user-accounts__buttons-area">
            <div>
              {uid && !isSessionUser && (
                <LinkButton
                  iconBefore={<DefaultButtonIcon type={IconType.Bin} />}
                  onClick={() => open()}
                  type={"button"}
                >
                  {t("wishlistPage.button.deleteProduct")}
                </LinkButton>
              )}
            </div>
            <div>
              <Link
                variant={LinkType.PRIMARY}
                href={MANAGE_USERS_PAGE}
                className="scha__my-account__form__buttons-area__cancel"
                data-test-id={"my-account-button-cancel"}
              >
                {t("page.completeRegistration.cancel")}
              </Link>
              <Button data-test-id={"my-account-button-submit"} onClick={handleSubmit((data) => onSubmit(data, false))}>
                {t("text.company.save.button")}
              </Button>
            </div>
          </div>
        </form>
      </div>
      {modalOpen && (
        <ConfirmationModal
          label={t("addon.user.delete.confirmation")}
          okLabel={t("addon.user.delete.yes")}
          cancelLabel={t("addon.user.delete.no")}
          onOk={() => onDelete()}
          onCancel={() => close()}
        />
      )}

      {modalCategoryOpen && (
        <ModalConfirmation
          onClose={closeModalCategory}
          onSubmit={handleSubmit((data) => onSubmit(data, true))}
          selectedValue={customerCategoryLabel}
          inputField={t(`page.completeRegistration.customerCategory`)}
        />
      )}
      <Card className="mt-60">
        <div className="scha__edit-user-accounts__roles">
          <div className="scha__edit-user-accounts__roles__headline">{t("addedituser.role.label")}</div>
          {renderRoles()}
        </div>
      </Card>
    </div>
  );
};

export default EditUserAccountsPage;

function resolveCustomerCategoryForAccounts(
  addedValues: string[],
  defaultB2BUnit: string,
  serviceProviderAccounts: string[],
  distributorAccounts: string[],
  setValue: UseFormSetValue<UserAccountValues>,
) {
  if (addedValues !== undefined && addedValues.length > 0) {
    let unitToBeUsed = addedValues.find((account) => account === defaultB2BUnit);
    if (unitToBeUsed === undefined) {
      unitToBeUsed = addedValues[0];
    }
    if (serviceProviderAccounts.includes(unitToBeUsed)) {
      setValue("customerCategoryCode", SERVICE_PROVIDER_CATEGORY);
    } else if (distributorAccounts.includes(unitToBeUsed)) {
      setValue("customerCategoryCode", DISTRIBUTOR_CATEGORY);
    } else {
      setValue("customerCategoryCode", CUSTOMER_CATEGORY);
    }
  } else {
    setValue("customerCategoryCode", CUSTOMER_CATEGORY);
  }
}

type HelperLabelErrorMessageProps = JSX.IntrinsicElements["span"] & {
  error?: FieldError;
};

const HelperLabelErrorMessage = ({ error, ...rest }: HelperLabelErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return " ";
  }

  return <span {...rest}>{t(error.message)}</span>;
};

export function createEditUserAccountPage(data: EditUserAccountsDTO) {
  return <EditUserAccountsPage {...transformData(data)} />;
}
